import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Empty, relTime, Avatar } from '../../components/ui.jsx'

/**
 * Existe porque GitLab 16.3.9 NO tiene system hook de bloqueo de usuario: sin este botón,
 * alguien desactivado en GitLab conservaría acceso hasta que caducara su sesión.
 */
export function Sessions() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['sessions'], queryFn: api.sessions, refetchInterval: 15_000 })
  const revoke = useMutation({
    mutationFn: (id) => api.revoke(id, 'revocación manual desde la consola'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  })

  return (
    <div className="main" style={{ margin: '0 auto' }}>
      <h1 className="h1">Sesiones activas</h1>
      <p className="lede">
        Revocar corta el acceso al instante. GitLab 16.3 no avisa cuando se bloquea a un usuario,
        así que este es el mecanismo para cortarle cuando alguien deja el equipo.
      </p>

      {q.isPending && <Spinner />}
      {q.data?.length === 0 && <Empty title="Sin sesiones">No hay nadie con sesión activa.</Empty>}

      {q.data?.length > 0 && (
        <div className="card">
          <div className="table-scroll">
            <table className="t">
              <thead>
                <tr><th>Usuario</th><th>Sesiones</th><th>Última actividad</th><th></th></tr>
              </thead>
              <tbody>
                {q.data.map((s) => (
                  <tr key={s.gitlab_user_id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={s.actor_name} email={s.actor_email} />
                        <div>
                          <div>{s.actor_name ?? s.actor_email}</div>
                          <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>{s.actor_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="num">{s.sesiones}</td>
                    <td style={{ color: 'var(--fg-2)' }}>{relTime(s.ultima_actividad)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn danger sm"
                              onClick={() => revoke.mutate(s.gitlab_user_id)}
                              disabled={revoke.isPending}>
                        Revocar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {revoke.isError && (
        <div className="alert error" style={{ marginTop: 14 }}>{revoke.error.message}</div>
      )}

      <TokensMcp />
    </div>
  )
}

/** Tokens del MCP (Claude Code/Desktop). Misma página que las sesiones: son el mismo problema. */
function TokensMcp() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['mcp-tokens'], queryFn: api.mcpTokens, refetchInterval: 30_000 })
  const revocar = useMutation({
    mutationFn: (id) => api.mcpRevoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-tokens'] }),
  })
  const tokens = q.data?.tokens ?? []

  return (
    <div className="section" style={{ marginTop: 34 }}>
      <h2 className="h2">Tokens MCP</h2>
      <p className="lede">
        Acceso desde Claude Code/Desktop. Un token hereda los permisos de su dueño en cada uso:
        revocarlo aquí lo corta al instante.
      </p>
      {q.isPending ? <Spinner /> : tokens.length === 0 ? (
        <Empty title="Sin tokens">Nadie ha autorizado todavía un cliente MCP.</Empty>
      ) : (
        <div className="card"><div className="table-scroll">
          <table className="t">
            <thead><tr><th>Usuario</th><th>Cliente</th><th>Creado</th><th>Último uso</th><th>Caduca</th><th /></tr></thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} style={t.revocado ? { opacity: .45 } : undefined}>
                  <td>{t.usuario}</td>
                  <td style={{ color: 'var(--fg-2)' }}>{t.cliente}</td>
                  <td style={{ color: 'var(--fg-2)' }}>{relTime(t.creado)}</td>
                  <td style={{ color: 'var(--fg-2)' }}>{t.ultimoUso ? relTime(t.ultimoUso) : 'nunca'}</td>
                  <td style={{ color: 'var(--fg-3)' }}>{relTime(t.caduca)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {t.revocado ? <span className="hint">revocado</span> : (
                      <button className="btn danger sm" disabled={revocar.isPending}
                              onClick={() => revocar.mutate(t.id)}>
                        Revocar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}
      {revocar.isError && (
        <div className="alert error" style={{ marginTop: 12 }}>{revocar.error.body?.detail ?? revocar.error.message}</div>
      )}
    </div>
  )
}
